import { IconActionButton } from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { InfoNotice } from '@/components/patterns/info-notice'
import { RadioSurface } from '@/components/patterns/selectable-surface'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { InputGroup } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { Can, useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  buildUpdateDiscountPayload,
  DiscountAvailability,
  DiscountTarget,
  DiscountType,
  DiscountUsagePolicy,
  formValuesFromDiscount,
  normalizeCodeInput,
  updateDiscountSchema,
} from '@/lib/services/discounts/discounts.schema'
import {
  useDeleteDiscountMutation,
  useUpdateDiscountMutation,
} from '@/lib/services/discounts/discounts.query'
import { cn, formatCurrency, getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import {
  CalendarDays,
  Check,
  Copy,
  DollarSign,
  Infinity as InfinityIcon,
  Percent,
  Repeat2,
  Tag,
  Trash2,
  UserRound,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  dateRangeLabel,
  deleteBlockedMessage,
  discountValueLabel,
  hasDeleteBlockingActivity,
  usagePolicyLabel,
  usedAtLabel,
} from './-discount-display'

export function getValidationErrors(error) {
  const errors = error?.issues ?? error?.response?.data?.errors ?? {}

  if (Array.isArray(errors)) {
    return errors.reduce((acc, issue) => {
      acc[issue.path?.[0] ?? 'form'] = issue.message
      return acc
    }, {})
  }

  return Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value])
  )
}

function FieldError({ children, id }) {
  if (!children) return null

  return (
    <p id={id} role="alert" className="text-xs text-destructive">
      {children}
    </p>
  )
}

function firstFieldError(field) {
  const error = field.state.meta.errors?.[0]

  return typeof error === 'string' ? error : (error?.message ?? '')
}

function LabeledInput({ label, error, children, errorId, htmlFor }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  )
}

async function copyDiscountCode(code) {
  if (!code) return

  try {
    await navigator.clipboard.writeText(code)
    toast.success('Discount code copied')
  } catch {
    toast.error('Could not copy discount code')
  }
}

export function CopyDiscountCodeButton({ code, className }) {
  return (
    <IconActionButton
      icon={Copy}
      label={`Copy ${code}`}
      variant="ghost"
      className={cn('rounded-xl', className)}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void copyDiscountCode(code)
      }}
    />
  )
}

const discountIconShellBaseClassName =
  'flex shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground ring-1 ring-inset ring-border/70'
const discountSelectedIconShellClassName = 'text-primary ring-primary/25 dark:bg-background/40'

function SegmentedChoice({ ariaLabel, options, value, onChange, className, disabled = false }) {
  const orientation = options.length === 3 ? 'responsive' : 'horizontal'

  return (
    <SegmentedControl
      role="radiogroup"
      aria-label={ariaLabel}
      orientation={orientation}
      className={cn('w-full', options.length === 2 && 'grid grid-cols-2', className)}
    >
      {options.map((option) => {
        const Icon = option.icon
        const isSelected = value === option.value

        return (
          <SegmentedControlItem
            key={option.value}
            type="button"
            role="radio"
            selected={isSelected}
            orientation={orientation}
            className={cn(
              'w-full',
              options.length === 3 && '@md/segmented-control:w-auto @md/segmented-control:flex-1'
            )}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {Icon ? <Icon className="size-4 shrink-0 self-center" /> : null}
            <span className="min-w-0 truncate leading-none">{option.label}</span>
          </SegmentedControlItem>
        )
      })}
    </SegmentedControl>
  )
}

function InlineAmountTypeChoice({ value, onChange, disabled = false }) {
  const options = [
    { value: DiscountType.Percent, label: '%', icon: Percent },
    { value: DiscountType.Amount, label: '$', icon: DollarSign },
  ]

  return (
    <SegmentedControl
      role="radiogroup"
      aria-label="Discount type"
      className="grid w-28 shrink-0 grid-cols-2"
    >
      {options.map((option) => {
        const Icon = option.icon
        const isSelected = value === option.value

        return (
          <SegmentedControlItem
            key={option.value}
            type="button"
            role="radio"
            selected={isSelected}
            size="icon"
            className="w-full"
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            <Icon className="size-4" />
            <span className="sr-only">
              {option.value === DiscountType.Percent ? 'Percent' : 'Amount'}
            </span>
          </SegmentedControlItem>
        )
      })}
    </SegmentedControl>
  )
}

function SelectableCardOption({
  description,
  disabled = false,
  icon: Icon,
  isSelected,
  label,
  onClick,
}) {
  return (
    <RadioSurface
      type="button"
      selected={isSelected}
      padding="compact"
      className="flex w-full items-start gap-3 text-left"
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className={cn(
          discountIconShellBaseClassName,
          'mt-0.5 size-9',
          isSelected && discountSelectedIconShellClassName
        )}
      >
        {isSelected ? <Check className="size-4" /> : <Icon className="size-4" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{label}</span>
        <span className="mt-0.5 block text-xs leading-5">{description}</span>
      </span>
    </RadioSurface>
  )
}

const discountTargetOptions = [
  { value: DiscountTarget.Items, label: 'Items' },
  { value: DiscountTarget.Delivery, label: 'Delivery' },
  { value: DiscountTarget.ItemsAndDelivery, label: 'Items + delivery' },
]

const availabilityOptions = [
  { value: DiscountAvailability.Always, label: 'Always', icon: Repeat2 },
  { value: DiscountAvailability.Range, label: 'Date range', icon: CalendarDays },
]

const usagePolicyOptions = [
  {
    value: DiscountUsagePolicy.Unlimited,
    label: 'Unlimited',
    description: 'Can be used again by eligible customers.',
    icon: InfinityIcon,
  },
  {
    value: DiscountUsagePolicy.OneTimeCode,
    label: 'One Time Code',
    description: 'Can be used once by one customer, then never again.',
    icon: Tag,
  },
  {
    value: DiscountUsagePolicy.LimitPerCustomer,
    label: 'Limit Per Customer',
    description: 'Limits completed checkout use by customer email.',
    icon: UserRound,
  },
]

export function DiscountForm({ form, onGenerateCode, isGeneratingCode, mode, isReadOnly = false }) {
  const values = useStore(form.store, (state) => state.values)
  const minimumScope =
    values.target === DiscountTarget.Delivery
      ? 'delivery'
      : values.target === DiscountTarget.ItemsAndDelivery
        ? 'items and delivery'
        : 'items'
  const fieldIds = {
    active: `${mode}-discount-active`,
    code: `${mode}-discount-code`,
    maxUsesPerCustomer: `${mode}-discount-max-uses-per-customer`,
    name: `${mode}-discount-name`,
    value: `${mode}-discount-value`,
    minimumPurchase: `${mode}-discount-minimum-purchase`,
  }

  const handleTargetChange = (target) => {
    if (isReadOnly) return

    if (target === values.target) {
      return
    }

    form.setFieldValue('target', target)
  }

  const handleAvailabilityChange = (availability) => {
    if (isReadOnly) return

    form.setFieldValue('availability', availability)

    if (availability === DiscountAvailability.Always) {
      form.setFieldValue('starts_on', '')
      form.setFieldValue('ends_on', '')
    }
  }

  return (
    <div className="@container/discount-form space-y-5">
      <form.AppField name="name">
        {(field) => {
          const error = firstFieldError(field)

          return (
            <LabeledInput
              label="Name"
              error={error}
              errorId={`${fieldIds.name}-error`}
              htmlFor={fieldIds.name}
            >
              <Input
                id={fieldIds.name}
                aria-describedby={error ? `${fieldIds.name}-error` : undefined}
                aria-invalid={Boolean(error)}
                value={field.state.value}
                disabled={isReadOnly}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
              />
            </LabeledInput>
          )
        }}
      </form.AppField>

      <form.AppField name="code">
        {(field) => {
          const error = firstFieldError(field)

          return (
            <LabeledInput
              label="Code"
              error={error}
              errorId={`${fieldIds.code}-error`}
              htmlFor={fieldIds.code}
            >
              <div className="flex gap-2">
                <Input
                  id={fieldIds.code}
                  aria-describedby={error ? `${fieldIds.code}-error` : undefined}
                  aria-invalid={Boolean(error)}
                  value={field.state.value}
                  disabled={isReadOnly || mode === 'edit'}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(normalizeCodeInput(event.target.value))}
                />
                {mode === 'create' ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 shrink-0"
                    onClick={onGenerateCode}
                    disabled={isReadOnly || isGeneratingCode}
                  >
                    Generate
                  </Button>
                ) : values.code ? (
                  <CopyDiscountCodeButton
                    code={values.code}
                    className="size-9 border border-input bg-background"
                  />
                ) : null}
              </div>
            </LabeledInput>
          )
        }}
      </form.AppField>

      {mode === 'create' ? (
        <>
          <div className="grid gap-4">
            <form.AppField name="target">
              {(field) => (
                <LabeledInput label="Applies to" error={firstFieldError(field)}>
                  <div className="@container/segmented-control">
                    <SegmentedChoice
                      ariaLabel="Applies to"
                      options={discountTargetOptions}
                      value={field.state.value}
                      onChange={handleTargetChange}
                      disabled={isReadOnly}
                    />
                  </div>
                  {values.target !== DiscountTarget.Delivery ? (
                    <Typography variant="description">
                      Items includes products and add-ons.
                    </Typography>
                  ) : null}
                </LabeledInput>
              )}
            </form.AppField>

            <form.AppField name="type">
              {(typeField) => (
                <form.AppField name="value">
                  {(valueField) => {
                    const error = firstFieldError(valueField) || firstFieldError(typeField)

                    return (
                      <LabeledInput
                        label={
                          typeField.state.value === DiscountType.Percent
                            ? 'Discount percent'
                            : 'Discount amount'
                        }
                        error={error}
                        errorId={`${fieldIds.value}-error`}
                        htmlFor={fieldIds.value}
                      >
                        <div className="flex items-center gap-3">
                          <InlineAmountTypeChoice
                            value={typeField.state.value}
                            onChange={typeField.handleChange}
                            disabled={isReadOnly}
                          />
                          <Input
                            id={fieldIds.value}
                            name="value"
                            aria-describedby={error ? `${fieldIds.value}-error` : undefined}
                            aria-invalid={Boolean(error)}
                            inputMode="decimal"
                            value={valueField.state.value}
                            disabled={isReadOnly}
                            onBlur={valueField.handleBlur}
                            onChange={(event) => valueField.handleChange(event.target.value)}
                          />
                        </div>
                      </LabeledInput>
                    )
                  }}
                </form.AppField>
              )}
            </form.AppField>
          </div>

          {values.type === DiscountType.Percent && Number(values.value) === 100 ? (
            <InfoNotice tone="warning" compact>
              100% off makes this free. Did you mean 10%?
            </InfoNotice>
          ) : null}

          <form.AppField name="minimum_purchase">
            {(field) => {
              const error = firstFieldError(field)
              return (
                <LabeledInput
                  label={
                    values.type === DiscountType.Amount
                      ? 'Minimum purchase'
                      : 'Minimum purchase (optional)'
                  }
                  htmlFor={fieldIds.minimumPurchase}
                  error={error}
                  errorId={`${fieldIds.minimumPurchase}-error`}
                >
                  <InputGroup
                    id={fieldIds.minimumPurchase}
                    name="minimum_purchase"
                    inputMode="decimal"
                    startAdornment={<DollarSign aria-hidden="true" />}
                    value={field.state.value}
                    placeholder={values.type === DiscountType.Percent ? 'No minimum' : '50.00'}
                    aria-invalid={Boolean(error)}
                    aria-describedby={`${fieldIds.minimumPurchase}-help${error ? ` ${fieldIds.minimumPurchase}-error` : ''}`}
                    disabled={isReadOnly}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  <Typography variant="description" id={`${fieldIds.minimumPurchase}-help`}>
                    Counts {minimumScope} before the discount. Taxes, tips, and other fees don’t
                    count.
                  </Typography>
                </LabeledInput>
              )
            }}
          </form.AppField>

          <Typography variant="body" weight="semibold" aria-live="polite">
            {discountValueLabel(values)}.
          </Typography>

          <form.AppField name="usage_policy">
            {(field) => (
              <LabeledInput label="Use limit" error={firstFieldError(field)}>
                <div className="grid gap-2" role="radiogroup" aria-label="Use limit">
                  {usagePolicyOptions.map((option) => (
                    <SelectableCardOption
                      key={option.value}
                      description={option.description}
                      icon={option.icon}
                      isSelected={field.state.value === option.value}
                      label={option.label}
                      onClick={() => field.handleChange(option.value)}
                      disabled={isReadOnly}
                    />
                  ))}
                </div>
              </LabeledInput>
            )}
          </form.AppField>

          {values.usage_policy === DiscountUsagePolicy.LimitPerCustomer ? (
            <form.AppField name="max_uses_per_customer">
              {(field) => {
                const error = firstFieldError(field)

                return (
                  <LabeledInput
                    label="Uses per customer"
                    error={error}
                    errorId={`${fieldIds.maxUsesPerCustomer}-error`}
                    htmlFor={fieldIds.maxUsesPerCustomer}
                  >
                    <Input
                      id={fieldIds.maxUsesPerCustomer}
                      aria-describedby={error ? `${fieldIds.maxUsesPerCustomer}-error` : undefined}
                      aria-invalid={Boolean(error)}
                      type="number"
                      min="1"
                      value={field.state.value}
                      disabled={isReadOnly}
                      onBlur={field.handleBlur}
                      onChange={(event) => field.handleChange(event.target.value)}
                    />
                  </LabeledInput>
                )
              }}
            </form.AppField>
          ) : null}

          <InfoNotice title="Subscription orders" tone="neutral" compact>
            For subscriptions, this code applies to the first order only. Future orders won’t
            receive the discount, regardless of the use limit.
          </InfoNotice>
        </>
      ) : null}

      <div className="space-y-4">
        <div className="sm:max-w-[18rem]">
          <form.AppField name="availability">
            {(field) => (
              <LabeledInput label="Availability" error={firstFieldError(field)}>
                <SegmentedChoice
                  ariaLabel="Availability"
                  options={availabilityOptions}
                  value={field.state.value}
                  onChange={handleAvailabilityChange}
                  disabled={isReadOnly}
                />
              </LabeledInput>
            )}
          </form.AppField>
        </div>

        {values.availability === DiscountAvailability.Range ? (
          <form.AppField name="starts_on">
            {(startField) => (
              <form.AppField name="ends_on">
                {(endField) => (
                  <LabeledInput
                    label="Date range"
                    error={firstFieldError(startField) || firstFieldError(endField)}
                  >
                    <ReportDateRangeFilterBar
                      className="justify-start"
                      triggerClassName="w-full"
                      disabled={isReadOnly}
                      initialFilters={{
                        start_date: startField.state.value,
                        end_date: endField.state.value,
                      }}
                      presetKeys={[]}
                      showTriggerChevron
                      onApply={({ start_date, end_date }) => {
                        startField.handleChange(start_date)
                        endField.handleChange(end_date)
                      }}
                    />
                  </LabeledInput>
                )}
              </form.AppField>
            )}
          </form.AppField>
        ) : null}
      </div>

      <form.AppField name="is_active">
        {(field) => (
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor={fieldIds.active}>Active</Label>
              <Typography variant="caption" className="block">
                Inactive codes cannot be applied.
              </Typography>
            </div>
            <Switch
              id={fieldIds.active}
              checked={field.state.value}
              disabled={isReadOnly}
              onCheckedChange={field.handleChange}
            />
          </div>
        )}
      </form.AppField>
    </div>
  )
}

function DiscountStats({ discount }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Card size="sm">
        <CardContent size="sm">
          <Typography variant="caption">Sales before discount</Typography>
          <Typography variant="h4">
            {formatCurrency(discount.attributed_order_value ?? 0)}
          </Typography>
        </CardContent>
      </Card>
      <Card size="sm">
        <CardContent size="sm">
          <Typography variant="caption">Customer savings</Typography>
          <Typography variant="h4">{formatCurrency(discount.discount_cost ?? 0)}</Typography>
        </CardContent>
      </Card>
      <Card size="sm" surface="brandSubtle">
        <CardContent size="sm">
          <Typography variant="caption">Sales after discount</Typography>
          <Typography variant="h4">{formatCurrency(discount.net_order_value ?? 0)}</Typography>
        </CardContent>
      </Card>
    </div>
  )
}

export function EditDiscountForm({ discount, onClose }) {
  const updateDiscount = useUpdateDiscountMutation()
  const deleteDiscount = useDeleteDiscountMutation()
  const { isReadOnly } = useIsReadOnly()
  const [deleteError, setDeleteError] = useState(null)
  const deleteBlocked = hasDeleteBlockingActivity(discount)
  const form = useAppForm({
    defaultValues: formValuesFromDiscount(discount),
    validators: { onSubmit: updateDiscountSchema },
    onSubmit: async ({ value, formApi }) => {
      if (isReadOnly) return

      setDeleteError(null)

      try {
        await updateDiscount.mutateAsync({
          discountId: discount.id,
          ...buildUpdateDiscountPayload(value),
        })
        toast.success('Discount code updated')
        onClose()
      } catch (error) {
        const validationErrors = getValidationErrors(error)

        if (Object.keys(validationErrors).length > 0) {
          formApi.setErrorMap({ onSubmit: { fields: validationErrors } })
        } else {
          toast.error('Discount code could not be updated', {
            description: getErrorMessage(error) || 'Please try again.',
          })
        }
      }
    },
  })

  const handleDelete = async (canDeleteDiscount) => {
    if (!canDeleteDiscount) return

    setDeleteError(null)

    try {
      await deleteDiscount.mutateAsync({ discountId: discount.id })
      toast.success('Discount code deleted')
      onClose()
    } catch (error) {
      const validationErrors = getValidationErrors(error)

      setDeleteError(
        validationErrors.discount ??
          validationErrors.form ??
          getErrorMessage(error) ??
          'Discount code could not be deleted.'
      )
    }
  }

  return (
    <form.AppForm>
      <form.Form className="flex min-h-0 flex-1 flex-col">
        <SheetHeader className="border-b px-6 py-4 text-left">
          <SheetTitle>{discount.code}</SheetTitle>
          <SheetDescription>{discountValueLabel(discount)}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <DiscountStats discount={discount} />

          <div className="grid gap-3 rounded-md border p-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Used</span>
              <span className="font-medium">{discount.usage_count ?? 0}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">In carts</span>
              <span className="font-medium">{discount.active_carts_count ?? 0}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Use limit</span>
              <span className="font-medium">{usagePolicyLabel(discount)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Availability</span>
              <span className="font-medium">{dateRangeLabel(discount)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">First used</span>
              <span className="font-medium">{usedAtLabel(discount.first_used_at)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Last used</span>
              <span className="font-medium">{usedAtLabel(discount.last_used_at)}</span>
            </div>
          </div>

          <DiscountForm mode="edit" form={form} isReadOnly={isReadOnly} />
        </div>
        <SheetFooter className="flex-row justify-between gap-3 border-t px-6 py-4">
          <div className="min-w-0 space-y-2">
            <Can do="delete" on="discounts" passThrough>
              {(canDeleteDiscount) =>
                !canDeleteDiscount ? (
                  <span />
                ) : deleteBlocked ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex" tabIndex={0}>
                        <Button
                          type="button"
                          variant="destructive"
                          disabled
                          className="pointer-events-none"
                        >
                          <Trash2 />
                          Delete
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64">
                      {deleteBlockedMessage(discount)}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <ConfirmationDialog
                    variant="destructive"
                    title="Delete discount code?"
                    description={`Delete ${discount.code}? Past reporting stays connected to existing orders, but this code will no longer be available.`}
                    confirmText="Delete code"
                    pendingText="Deleting..."
                    isPending={deleteDiscount.isPending}
                    onConfirm={() => handleDelete(canDeleteDiscount)}
                    trigger={
                      <Button
                        type="button"
                        variant="destructive"
                        disabled={deleteDiscount.isPending}
                      >
                        <Trash2 />
                        Delete
                      </Button>
                    }
                  />
                )
              }
            </Can>
            <FieldError>{deleteError}</FieldError>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {isReadOnly ? null : (
              <Button type="submit" disabled={updateDiscount.isPending}>
                Save
              </Button>
            )}
          </div>
        </SheetFooter>
      </form.Form>
    </form.AppForm>
  )
}
